/**
 * LocationPicker - Wrapper around generic EntityPicker
 * 
 * Provides location-specific configuration and maintains backward compatibility
 * with any code that imports LocationPickerField or LocationPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Location-specific configuration ────────────────────────────────────────────
export const LOCATION_CONFIG = {
  apiEndpoint: "/api/Location/active",
  entityName: "Location",
  idField: "id",
  displayFields: {
    primary: "description"
  },
  tableHeaders: ["Description"],
  tableAccessors: ["description"],
  searchFields: [
    { label: "Description", field: "description", index: 0 },
  ],
  placeholder: "--Select location--"
};

// ── LocationPickerField wrapper ────────────────────────────────────────────────
/**
 * LocationPickerField
 * 
 * Usage:
 * <LocationPickerField
 *   label="Location"
 *   locationId={form.locationId}
 *   displayValue={form.locationDescription}
 *   onSelect={(location) => {
 *     set("locationId", location.id);
 *     set("locationDescription", location.description);
 *   }}
 *   onClear={() => {
 *     set("locationId", "");
 *     set("locationDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function LocationPickerField({
  locationId,           // location-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={LOCATION_CONFIG}
      entityId={locationId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── LocationPickerModal wrapper ────────────────────────────────────────────────
/**
 * LocationPickerModal
 * 
 * Usage (standalone):
 * <LocationPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(location) => {
 *     set("locationId", location.id);
 *     set("locationDescription", location.description);
 *   }}
 * />
 */
export function LocationPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={LOCATION_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { LocationPickerField as default };